import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { Class as ClassIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { classService } from '../../services/classService';
import { Class } from '../../types';
import SettingsList, { SettingsTile } from '../common/SettingsList';
import SettingsDialog from '../common/SettingsDialog';
import { ToneChip } from '../common/StatusChip';
import { parseNameList } from '../../utils/nameList';

/** Every class the school has — a settings list is never paged. */
const ALL = 500;

/**
 * Promotion order: the promotion engine walks active classes by displayOrder
 * to find the "next" class (ties fall back to name). Inactive classes are out
 * of the progression, so they sit at the end, unnumbered.
 */
const byPromotionOrder = (a: Class, b: Class) =>
  Number(b.isActive) - Number(a.isActive)
  || (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  || a.name.localeCompare(b.name);

const ClassesSettings: React.FC = () => {
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const writable = canWrite('settings_classes');

  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Class | null>(null);
  const [form, setForm] = useState({ name: '', description: '', isTerminal: false, isActive: true });
  const [saving, setSaving] = useState(false);

  // The full list, never searched server-side, so a reorder always renumbers every class.
  const load = useCallback(async () => {
    try {
      const res = await classService.getClasses({ limit: ALL });
      setClasses([...res.classes].sort(byPromotionOrder));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not load classes', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Position in the whole progression, independent of any search filter.
  const position = useMemo(
    () => new Map(classes.filter(c => c.isActive).map((c, i) => [c._id, i + 1])),
    [classes],
  );

  const edit = (c: Class) => {
    setEditing(c);
    setForm({ name: c.name, description: c.description ?? '', isTerminal: c.isTerminal === true, isActive: c.isActive });
  };

  const save = async () => {
    if (!editing || !form.name.trim()) return;
    setSaving(true);
    try {
      await classService.updateClass(editing._id, { ...form, name: form.name.trim() });
      enqueueSnackbar('Class saved', { variant: 'success' });
      setEditing(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Could not save this class', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsList
        rows={classes}
        loading={loading}
        rowKey={c => c._id}
        name={c => c.name}
        subtitle={c => c.description || undefined}
        leading={c => <SettingsTile>{position.get(c._id) ?? '–'}</SettingsTile>}
        muted={c => !c.isActive}
        meta={c => (
          <>
            {c.isTerminal && <ToneChip label="Terminal" tone="highlight" />}
            {!c.isActive && <ToneChip label="Inactive" tone="neutral" />}
          </>
        )}
        searchText={c => `${c.name} ${c.description ?? ''}`}
        canWrite={writable}
        onEdit={edit}
        // Only active classes take part in the progression; the backend rejects inactive ids.
        reorder={keys => classService.reorderClasses(keys.filter(k => position.has(k)))}
        movable={c => c.isActive}
        remove={{
          run: c => classService.deleteClass(c._id),
          noun: 'class',
          consequence: () => 'The class will be removed. A class that has curriculum subjects, enrolled students or admission applications cannot be deleted — make it inactive instead.',
        }}
        quickAdd={{
          noun: { one: 'class', many: 'classes' },
          placeholder: 'Add classes, e.g. Class 1-10 or Nursery, KG, Prep',
          // New classes join the end of the progression, in the order typed.
          create: name => classService.createClass({ name }),
          ranges: true,
          presets: [
            { label: 'Class 1–10', names: parseNameList('Class 1-10', { ranges: true }) },
            { label: 'Early years', names: ['Playgroup', 'Nursery', 'KG', 'Prep'] },
            { label: 'O & A Levels', names: ['O Level I', 'O Level II', 'O Level III', 'AS Level', 'A2 Level'] },
          ],
        }}
        reload={load}
        empty={{
          icon: <ClassIcon />,
          title: 'No classes yet',
          description: 'Add classes in the order students progress through them — several at once if you like. Promotion moves students from each class to the next.',
        }}
      />

      <SettingsDialog
        open={!!editing}
        title="Edit class"
        onClose={() => setEditing(null)}
        onSave={save}
        saving={saving}
        saveDisabled={!form.name.trim()}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          error={!form.name.trim()}
          helperText={!form.name.trim() ? 'A class needs a name' : ' '}
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Optional"
          multiline
          minRows={2}
          fullWidth
        />
        {/* Drives the promotion engine's default decision for passing students. */}
        <FormControlLabel
          control={<Switch checked={form.isTerminal} onChange={e => setForm(f => ({ ...f, isTerminal: e.target.checked }))} />}
          label={
            <Stack spacing={0.25}>
              <Typography variant="body2" fontWeight={600}>Terminal class</Typography>
              <Typography variant="caption" color="text.secondary">
                Final class in the school's progression. Passing students will be defaulted to Graduate instead of Promote at promotion time.
              </Typography>
            </Stack>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
          label="Active — part of the promotion order"
        />
      </SettingsDialog>
    </>
  );
};

export default ClassesSettings;
